/**
 * Client-side search over the fetched tree.
 *
 * In the browser rather than on the server because the whole corpus is already
 * here — searching it needs no round trip and works while typing. Umlauts are
 * folded so "Gultigkeit" finds "Gültigkeit", and an article has to contain every
 * query word to match, which keeps a two-word query from returning half the
 * helpdesk. Past a few hundred articles this belongs on the server.
 */

import type { HelpContent } from "./api/types"
import { articleText, flattenArticles, type ArticleLocation } from "./content"

const UMLAUTS: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  ß: "ss",
}

/** Lowercase and expand umlauts the standard way: ü → ue. */
export function foldForSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/[äöüß]/g, (char) => UMLAUTS[char] ?? char)
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
}

/**
 * Both spellings of a German word, because readers type both: "Gültigkeit"
 * reached as "gueltigkeit" by anyone using the standard transliteration, and as
 * "gultigkeit" by anyone just dropping the umlaut. Indexing both is what makes a
 * search succeed either way; folding only one of them silently loses the other.
 */
function searchableText(value: string): string {
  const stripped = value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
  return `${foldForSearch(value)} ${stripped}`
}

export interface HelpdeskSearchHit extends ArticleLocation {
  score: number
}

export function searchArticles(
  content: HelpContent,
  query: string,
): HelpdeskSearchHit[] {
  const tokens = foldForSearch(query).split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return []

  const hits: HelpdeskSearchHit[] = []
  for (const entry of flattenArticles(content)) {
    const title = searchableText(entry.article.title)
    const description = searchableText(entry.article.description)
    const body = searchableText(articleText(entry.article))

    let score = 0
    const matchesEvery = tokens.every((token) => {
      if (title.includes(token)) {
        score += 3
        return true
      }
      if (description.includes(token)) {
        score += 2
        return true
      }
      if (body.includes(token)) {
        score += 1
        return true
      }
      return false
    })

    if (matchesEvery) hits.push({ ...entry, score })
  }

  return hits.sort(
    (a, b) =>
      b.score - a.score || a.article.title.localeCompare(b.article.title),
  )
}
